import { useCallback, useState } from 'react';

import AccountSignatureEditor from '@/components/integrations/AccountSignatureEditor';
import type { OutlookStatusAccount } from '@/hooks/integrations/microsoft/useOutlookStatus';
import { ButtonList } from '@/lib/base/button-list';

interface OutlookSignatureItemProps {
  account: OutlookStatusAccount;
  orgId: string;
  userId: number;
  canModify: boolean;
  isExpanded: boolean;
  onToggle: (entityId: string) => void;
}

/**
 * A collapsible per-account Outlook signature row. Collapsed by default; the
 * expanded body renders the same `AccountSignatureEditor` the Email & Calendar
 * account cards use, so the signature is editable from both places. Gmail
 * accounts have no row here: their signature is fetched live from Gmail at
 * send time. Keeping the editor unmounted while collapsed also defers its
 * CRM-object fetch until the user actually opens it.
 */
function OutlookSignatureItem({
  account,
  orgId,
  userId,
  canModify,
  isExpanded,
  onToggle,
}: OutlookSignatureItemProps) {
  const entityId = account.outlook_mail_entity_id;
  const handleToggle = useCallback(
    () => onToggle(entityId),
    [onToggle, entityId]
  );

  return (
    <>
      <ButtonList.Item onAction={handleToggle} showChevron>
        <p className='text-sm font-medium text-fg-primary mb-0.5'>
          {account.email ?? 'Outlook account'}
        </p>
        <p className='text-sm text-fg-tertiary'>
          {isExpanded ? 'Hide signature' : 'Edit signature'}
        </p>
      </ButtonList.Item>
      {isExpanded && (
        <div className='px-4 py-3'>
          <AccountSignatureEditor
            orgId={orgId}
            entityId={entityId}
            userId={userId}
            canModify={canModify}
          />
        </div>
      )}
    </>
  );
}

interface OutlookSignatureListProps {
  orgId: string;
  userId: number;
  accounts: OutlookStatusAccount[];
  canModify: boolean;
}

/**
 * Per-account Outlook signature rows, collapsed by default with only one
 * expanded at a time. Owns the expand/collapse state internally.
 */
export function OutlookSignatureList({
  orgId,
  userId,
  accounts,
  canModify,
}: OutlookSignatureListProps) {
  const [expandedSignatureId, setExpandedSignatureId] = useState<string | null>(
    null
  );
  const handleToggleSignature = useCallback((entityId: string) => {
    setExpandedSignatureId(current => (current === entityId ? null : entityId));
  }, []);

  return (
    <>
      {accounts.map(account => (
        <OutlookSignatureItem
          key={account.outlook_mail_entity_id}
          account={account}
          orgId={orgId}
          userId={userId}
          canModify={canModify}
          isExpanded={expandedSignatureId === account.outlook_mail_entity_id}
          onToggle={handleToggleSignature}
        />
      ))}
    </>
  );
}
